import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { MachineNameAndId } from "@common/types/server-list-types";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { getMachinesUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-machines-use-case";
import { patchTicketMachineUseCase } from "@src/cloud-connect/support/use-cases/patch-ticket-machine-use-case";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";

interface UseTicketMachineSelectionResult {
  machineOptions: MachineNameAndId[];
  selectedMachine: MachineNameAndId | null;
  isLoading: boolean;
  isSelectionFixed: boolean;
  isPatchingMachine: boolean;
  isSSEUnavailable: boolean;
  onSelectionChange: (machine: MachineNameAndId | null) => void;
  confirmMachineSelection: () => Promise<void>;
}

export const useTicketMachineSelection = (
  ticketId: string | undefined,
  ticketMachineId: string | null | undefined,
  callbackForRefresh: () => void,
): UseTicketMachineSelectionResult => {
  const [machines, setMachines] = useState<MachineNameAndId[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPatchingMachine, setIsPatchingMachine] = useState(false);
  const [pendingMachine, setPendingMachine] = useState<MachineNameAndId | null>(
    null,
  );
  const patchRequestGenerationRef = useRef(0);
  const machineRequestGenerationRef = useRef(0);
  const patchInProgressRef = useRef(false);
  const activeTicketIdRef = useRef(ticketId);

  const isMachineAssigned = Boolean(ticketMachineId);
  const isSelectionFixed = isMachineAssigned;
  const isSSEUnavailable = useSSEUnavailable("service-support");
  const isSSEUnavailableRef = useRef(isSSEUnavailable);

  useLayoutEffect(() => {
    activeTicketIdRef.current = ticketId;
    patchRequestGenerationRef.current += 1;
    machineRequestGenerationRef.current += 1;
    setPendingMachine(null);
    patchInProgressRef.current = false;
    setIsPatchingMachine(false);

    return () => {
      patchRequestGenerationRef.current += 1;
      patchInProgressRef.current = false;
      if (activeTicketIdRef.current === ticketId) {
        activeTicketIdRef.current = undefined;
      }
    };
  }, [ticketId]);

  useLayoutEffect(() => {
    isSSEUnavailableRef.current = isSSEUnavailable;
  }, [isSSEUnavailable]);

  const loadMachines = useCallback(async () => {
    const requestGeneration = ++machineRequestGenerationRef.current;
    setIsLoading(true);
    const result = await getMachinesUseCase();

    if (requestGeneration !== machineRequestGenerationRef.current) return;

    if (!result.success || !result.data) {
      errorSnackbar(result.error);
      setIsLoading(false);
      return;
    }

    setMachines(
      result.data.map((machine) => ({
        name: machine.name,
        machine_id: machine.id,
      })),
    );
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!ticketId) return;
    void loadMachines();
    return () => {
      machineRequestGenerationRef.current += 1;
    };
  }, [ticketId, loadMachines]);

  const selectedMachine: MachineNameAndId | null = ticketMachineId
    ? {
        name:
          machines.find((machine) => machine.machine_id === ticketMachineId)
            ?.name || ticketMachineId,
        machine_id: ticketMachineId,
      }
    : pendingMachine;

  const onSelectionChange = (machine: MachineNameAndId | null) => {
    if (
      activeTicketIdRef.current !== ticketId ||
      isSelectionFixed ||
      isPatchingMachine ||
      patchInProgressRef.current ||
      isSSEUnavailableRef.current
    ) {
      return;
    }
    setPendingMachine(machine);
  };

  const confirmMachineSelection = async () => {
    if (
      !ticketId ||
      activeTicketIdRef.current !== ticketId ||
      isSelectionFixed ||
      isPatchingMachine ||
      patchInProgressRef.current ||
      isSSEUnavailableRef.current ||
      !pendingMachine
    ) {
      return;
    }

    const requestTicketId = ticketId;
    const requestGeneration = patchRequestGenerationRef.current;
    patchInProgressRef.current = true;
    setIsPatchingMachine(true);
    const result = await patchTicketMachineUseCase(
      pendingMachine.machine_id,
      requestTicketId,
    );

    if (
      requestGeneration !== patchRequestGenerationRef.current ||
      activeTicketIdRef.current !== requestTicketId
    ) {
      return;
    }

    setIsPatchingMachine(false);

    if (!result.success) {
      patchInProgressRef.current = false;
      errorSnackbar(result.error);
      return;
    }
    callbackForRefresh();
  };

  return {
    machineOptions: machines,
    selectedMachine,
    isLoading,
    isSelectionFixed,
    isPatchingMachine,
    isSSEUnavailable,
    onSelectionChange,
    confirmMachineSelection,
  };
};
